<template>
  <div>
    <div v-if="loading" min-height="4px">
      <v-progress-linear
        color="primary lighten-3"
        height="4"
        active
        indeterminate
      />
    </div>
    <div class="pt-1" v-else></div>
    <v-row class="px-md-2 mb-3" no-gutters>
      <v-col cols="12" md="2" class="d-flex align-center">
        <v-breadcrumbs
          :large="true"
          :items="[
            {
              text: 'Datasets',
              disabled: false,
              href: '/datasets',
            },
            {
              text: dataset.name || $route.params.datasetUid,
              disable: true,
            },
          ]"
          divider=">"
          class="pa-0"
          width="100%"
        >
          <template v-slot:item="props">
            <v-breadcrumbs-item
              :href="props.item.href"
              :class="[props.item.disabled && 'disabled']"
              @click.prevent="$router.push(props.item.href)"
              >{{ props.item.text }}</v-breadcrumbs-item
            >
          </template>
        </v-breadcrumbs>
      </v-col>
      <v-col cols="12" md="6" class="pa-0 d-flex align-center">
        <div class="d-flex align-center" v-if="search.filters.length > 0">
          Filtros:
          <filter-bar :filters="search.filters" @removeFilter="removeFilter" />
        </div>
      </v-col>
      <v-col cols="12" md="4" class="pr-0">
        <v-btn-toggle v-model="view" class="d-flex justify-end" mandatory>
          <v-btn
            class="text-capitalize"
            width="40%"
            value="dashboard1"
            small
            outlined
          >
            <v-icon size="16" left>mdi-monitor-dashboard</v-icon>
            Dashboard 1
          </v-btn>
          <v-btn
            class="text-capitalize"
            width="40%"
            value="dashboard2"
            small
            outlined
          >
            <v-icon size="16" left>mdi-monitor-dashboard</v-icon>
            Dashboard 2
          </v-btn>
          <v-btn
            class="text-capitalize"
            width="20%"
            value="data"
            small
            outlined
          >
            <v-icon size="16" left>mdi-format-list-bulleted</v-icon>
            Datos
          </v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>
    <div v-if="dataset.status == 'available'">
      <v-row no-gutters>
        <v-col cols="12">
          <!-- Other option is use v-show and a key based on searchKey to re-render only when search criteria change -->
          <main-facts
            v-if="dashboard1Data && view == 'dashboard1'"
            :dashboardData="dashboard1Data"
            :filterOptions="filterOptions"
            @filter="doFilter"
            @select="doSelect"
            transition="scroll-x-transition"
          />
          <component
            v-if="dashboard2Data && view == 'dashboard2'"
            v-bind:is="dashboard2Layout"
            :dashboardData="dashboard2Data"
            :filterOptions="filterOptions"
            @filter="doFilter"
            @select="doSelect"
            transition="scroll-x-transition"
          />
          <Data
            v-if="dashboard2Data && view == 'data'"
            :dataset="dataset"
            :results="results"
            :filterOptions="filterOptions"
            @sort="doSort"
            @filter="doFilter"
            @select="doSelect"
            transition="scroll-x-transition"
          />
        </v-col>
      </v-row>
    </div>
    <div v-else-if="dataset.status == 'no-data'">
      <v-row>
        <v-col
          cols="12"
          style="min-height: 80vh"
          class="d-flex align-center justify-center"
        >
          <v-card
            class="pa-8 d-flex align-center justify-center"
            min-height="120px"
            min-width="400px"
          >
            <v-card-text>
              <div class="d-flex align-center justify-center">
                <v-icon size="32">info_outlined</v-icon>
                <h2 class="font-weight-regular">No hay datos disponibles!</h2>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <div v-else>Loading...</div>
  </div>
</template>

<script>
import _debounce from "lodash.debounce";

import FilterBar from "@/components/dashboard/filters/FilterBar.vue";
import NineRows from "@/components/dashboard/layouts/NineRows.vue";
import ThreeTop from "@/components/dashboard/layouts/ThreeTop.vue";
import ThreeCharts from "@/components/dashboard/layouts/ThreeCharts.vue";
import FourCharts from "@/components/dashboard/layouts/FourCharts.vue";
import FiveCharts from "@/components/dashboard/layouts/FiveCharts.vue";
import SixCharts from "@/components/dashboard/layouts/SixCharts.vue";
import EightCharts from "@/components/dashboard/layouts/EightCharts.vue";
import MainFacts from "@/views/dashboard/custom/MainFacts.vue";

import Data from "@/components/dashboard/data/Data.vue";

import { getChartOptions } from "@/commons/chart-commons.js";
import { getFilterOptions } from "@/commons/filters.js";
import {
  reverseCategoryDisplayName,
  reverseTopicDisplayName,
  reversePolarityDisplayName,
} from "@/commons/util.js";
const hash = require("object-hash");

export default {
  data() {
    return {
      view: "dashboard",
      loading: true,
      dataset: {},
      metadata: {},
      dashboard1Data: undefined,
      dashboard2Data: undefined,
      dashboard2Definition: undefined,
      search: {
        query: "",
        interval: "",
        sort: "_score",
        startDate: "",
        endDate: "",
        filters: [],
        selects: [],
      },
      interval: "",
      filterOptions: {},
      results: [],
    };
  },
  components: {
    FilterBar,
    NineRows,
    ThreeCharts,
    ThreeTop,
    FourCharts,
    FiveCharts,
    SixCharts,
    EightCharts,
    MainFacts,
    Data,
  },
  mounted() {
    console.log("mounted");
    this.$eventBus.$on("doKeywordSearch", this.doKeywordSearch);
    this.$eventBus.$on("doDateSearch", this.doDateSearch);
    this.getMetatada();
  },
  beforeDestroy() {
    console.log("beforeDestroy");
    this.$eventBus.$emit("clearSearchInput");
  },
  methods: {
    debounceSearch: _debounce(function () {
      this.doSearch();
    }, 250),
    checkStorage() {
      let metadataSize = 0;
      for (var i = 0, len = sessionStorage.length; i < len; ++i) {
        let storageKey = sessionStorage.key(i);
        if (storageKey.startsWith("pcdb-")) {
          metadataSize++;
        }
      }
      if (metadataSize >= 10) {
        console.log(
          "cleaning storage, metadata max size exceeded:",
          metadataSize
        );
        sessionStorage.clear();
      }
    },
    getMetatada() {
      this.checkStorage();
      let metadata = sessionStorage.getItem(this.metadataKey);
      if (metadata === null) {
        console.log("Getting metadata from the server");
        this.$http
          .get(`/api/datasets/${this.datasetUid}/metadata`)
          .then((response) => {
            console.log("getMetadata", response);
            let tempMetadata = response.data.metadata;
            let entitiesFreq = {};
            Object.keys(tempMetadata.entities.global_frequencies).forEach(
              (key) => {
                let value = tempMetadata.entities.global_frequencies[key];
                if (value > 1) {
                  entitiesFreq[key] = value;
                }
              }
            );
            tempMetadata.entities.global_frequencies = entitiesFreq;
            this.metadata = Object.freeze(tempMetadata);
            sessionStorage.setItem(
              this.metadataKey,
              JSON.stringify(this.metadata)
            );
            this.getDataset();
          });
      } else {
        console.log("Getting metadata from the storage", metadata);
        this.metadata = Object.freeze(JSON.parse(metadata));
        this.getDataset();
      }
    },
    getDataset() {
      this.$http.get(`/api/datasets/${this.datasetUid}`).then((response) => {
        console.log("getDataset", response);
        this.dataset = response.data.dataset;
        if (this.dataset.status == "available") {
          this.doSearch();
        }
      });
    },
    doSearch() {
      console.log("doSearch");
      this.loading = true;
      const { query, sort, startDate, endDate } = this.search;
      const interval =
        this.interval || Array.isArray(this.metadata.interval)
          ? this.metadata.interval[0]
          : this.metadata.interval;
      let searchRequest = {
        query,
        interval,
        sort,
        startDate,
        endDate,
        author: [],
        categories: [],
        topics: [],
        polarity: [],
        entities: [],
      };

      this.search.filters.forEach((filter) => {
        if (filter.source == "chart") {
          if (filter.title == "Categorias") {
            searchRequest.categories.push(
              reverseCategoryDisplayName(filter.text)
            );
          }
          if (filter.title == "Topics") {
            searchRequest.topics.push(reverseTopicDisplayName(filter.text));
          }
          if (filter.title == "Polaridad") {
            searchRequest.polarity = [reversePolarityDisplayName(filter.text)];
          }
          if (filter.title == "Autores") {
            searchRequest.author = [filter.text];
          }
        }
        if (filter.source == "label") {
          if (filter.type == "CATEGORY") {
            searchRequest.categories.push(filter.key);
          }
          if (filter.type == "TOPIC") {
            searchRequest.topics.push(filter.key);
          }
          if (filter.type == "POLARITY") {
            searchRequest.polarity = [filter.key];
          }
          if (
            ["PER", "ORG", "LOC", "HASHTAG", "MENTION"].includes(filter.type)
          ) {
            searchRequest["entities"].push(filter.key);
          }
        }
      });

      this.search.selects.forEach((entry) => {
        if (entry) {
          if (entry.name in searchRequest) {
            searchRequest[entry.name].push(entry.value);
          } else {
            searchRequest[entry.name] = [];
            searchRequest[entry.name].push(entry.value);
          }
        }
      });

      console.log("searchRequest", searchRequest);
      this.$http
        .post(`/api/datasets/${this.datasetUid}/search`, searchRequest)
        .then((response) => {
          console.log("searchResponse", response);
          if (response.data.status == "no-data") {
            this.dataset.status = "no-data";
            return;
          }
          if (response.data.status == "ok") {
            const chartOptions = getChartOptions({});
            const responseDataCopy = JSON.parse(JSON.stringify(response.data));
            this.dashboard1Definition = {
              name: "Hechos Nacionales",
              charts: [
                "category_topic",
                "googleMapMarkers",
                "category_activity",
              ],
              layout: "custom-main-facts",
            };
            let db1Data = {};
            this.dashboard1Definition.charts.forEach((chart, index) => {
              try {
                console.log("Processing chart", chart);
                db1Data["chart" + (index + 1)] = chartOptions[chart](
                  responseDataCopy, //Use a copy of the data here because slice modify in place
                  this.metadata
                );
              } catch (error) {
                console.error("Chart error", chart, error);
              }
            });
            this.dashboard1Data = db1Data;
            console.log("dashboard1Definition", this.dashboard1Definition);
            console.log("dashboard1Data", this.dashboard1Data);
            this.dashboard2Definition = response.data.dashboard_definition;

            let db2Data = {};
            this.dashboard2Definition.charts.forEach((chart, index) => {
              try {
                console.log("Processing chart", chart);
                db2Data["chart" + (index + 1)] = chartOptions[chart](
                  response.data,
                  this.metadata
                );
              } catch (error) {
                console.error("Chart error", chart, error);
              }
            });
            this.dashboard2Data = db2Data;
            console.log("dashboard2Definition", this.dashboard2Definition);
            console.log("dashboard2Data", this.dashboard2Data);

            this.results = response.data.results;
            this.filterOptions = getFilterOptions({
              metadata: this.metadata,
              searchResponse: response.data,
            });
            this.loading = false;
          }
        });
    },
    doKeywordSearch(keyword) {
      this.search.query = keyword;
      this.doSearch();
    },
    doDateSearch(dateRange) {
      if (dateRange) {
        let [date1, date2] = dateRange;
        if (new Date(date1) < new Date(date2)) {
          this.search.startDate = date1;
          this.search.endDate = date2;
        } else {
          this.search.startDate = date2;
          this.search.endDate = date1;
        }
      } else {
        this.search.startDate = "";
        this.search.endDate = "";
      }
      this.debounceSearch();
    },
    doFilter(filters) {
      console.log("doFilter", filters);
      filters.forEach((filter) => {
        if (filter.source == "wordcloud") {
          this.search.query = filter.text;
          this.$eventBus.$emit("setSearchInput", { query: filter.text });
          this.debounceSearch();
          return;
        }
        let filterFound = false;
        this.search.filters.forEach((entry) => {
          if (Object.prototype.hasOwnProperty.call(entry, "key")) {
            if (entry.key === filter.key) {
              filterFound = true;
            }
          } else {
            if (entry.text === filter.text) {
              filterFound = true;
            }
          }
        });
        if (!filterFound) {
          this.search.filters.push(filter);
          this.debounceSearch();
        }
      });
    },
    doSelect(selects) {
      this.search.selects = selects;
      this.debounceSearch();
    },
    doSort(sortBy) {
      if (sortBy) {
        this.search.sort = sortBy;
        this.debounceSearch();
      }
    },
    removeFilter(filter, i) {
      console.log("removeFilter", filter, i);
      this.search.filters.splice(i, 1);
      this.debounceSearch();
    },
    resetSearch() {
      this.search = {
        search: "",
        sort: "_score",
        author: [],
        categories: [],
        polarity: [],
        entities: [],
      };
    },
  },
  computed: {
    dashboard2Layout() {
      if (this.dashboard2Definition) {
        if (
          this.dashboard2Definition.layout
            .trim()
            .toLowerCase()
            .startsWith("custom-")
        ) {
          return this.dashboard2Definition.layout.slice(7);
        } else {
          switch (this.dashboard2Definition.layout) {
            case "3top":
              return "three-top";
            case "3rows":
              return "three-charts";
            case "4":
              return "four-charts";
            case "5":
              return "five-charts";
            case "6":
              return "six-charts";
            case "8":
              return "eight-charts";
            default:
              return "six-charts";
          }
        }
      }
      return "six-charts";
    },
    searchKey() {
      return hash(JSON.stringify(this.search));
    },
    datasetUid() {
      return "b1541d89-64c4-4c86-b02d-8b7aec34d347";
    },
    metadataKey() {
      return `pcdb-${this.datasetUid}`;
    },
  },
};
</script>

<style scoped>
.small {
  max-width: 600px;
  margin: 150px auto;
}
</style>
